import type { Adjustment } from '@app/shared/types/sf-data'
import type {
  AdjustmentState,
  AdjustmentFilterKeys,
  BaseAdjustmentParam,
} from '../types'
import { isAdjustmentFilterKey } from '../predicates'

import { defineStore } from 'pinia'

import { SfLog } from '@app/adapters/logger'
import { fetchAdjustmentsAndOrderBatch } from '@app/application/adjustments/utils'

export const useAdjustmentsStore = defineStore('adjustments', {
  state: (): AdjustmentState => ({
    tags: [],
    categories: [],
    adjustments: {},
    adjustmentOrder: [],
    ledgers: [],
    loading: {
      adjustments: false,
      ledger: false,
      submit: false,
    },
    copyType: 'normal',
    copyTypes: [],
    forwardAccounts: [],
    selectedAccountId: 'placeholder',
    selectedLedgerId: 'placeholder',
  }),
  actions: {
    checkAdjustment(id: number) {
      this.adjustments[id].checked = true
    },
    async fetchAdjustments(
      ledgerId: number,
      accountId: number | undefined,
      checkedAdjustments?: Adjustment[]
    ) {
      this.adjustments = {}
      this.adjustmentOrder = []
      this.loading.ledger = true

      try {
        let page = 1
        let loadMorePages = true

        while (loadMorePages) {
          const adjustmentsAndOrder = await fetchAdjustmentsAndOrderBatch(
            this.copyType,
            page,
            ledgerId,
            accountId,
            checkedAdjustments
          )

          this.adjustments = {
            ...this.adjustments,
            ...adjustmentsAndOrder.adjustments,
          }
          this.adjustmentOrder = this.adjustmentOrder.concat(
            adjustmentsAndOrder.adjustmentOrder
          )

          page = page + 1
          loadMorePages = adjustmentsAndOrder.adjustmentOrder.length > 0
        }
      } catch (e) {
        SfLog.error('Error fetching adjustments', { error: e })
      } finally {
        this.loading.ledger = false
      }
    },
    receiveInitialData(payload: {
      categories: Array<BaseAdjustmentParam>
      copyType: 'forward' | 'reverse' | 'normal'
      copyTypes: Array<{ id: string; name: string }>
      forwardAccounts: Array<BaseAdjustmentParam>
      ledgers: Array<{ id: number; period_name: string }>
      tags: Array<BaseAdjustmentParam>
    }) {
      this.categories = payload.categories
      this.copyType = payload.copyType
      this.copyTypes = payload.copyTypes
      this.forwardAccounts = payload.forwardAccounts
      this.ledgers = payload.ledgers
      this.tags = payload.tags
    },
    submitForm() {
      this.loading.submit = true
    },
    toggleAdjustment(id: number) {
      this.adjustments[id].checked = !this.adjustments[id].checked
    },
    toggleTag(payload: {
      checked: boolean
      key: AdjustmentFilterKeys
      filterId: number | string
    }) {
      const { checked, key, filterId } = payload

      const itemsToToggle = this.getAdjustmentIdsByFilter[key][filterId]

      itemsToToggle.forEach((id) => {
        if (checked) {
          this.checkAdjustment(id)
        } else {
          this.uncheckAdjustment(id)
        }
      })
    },
    uncheckAdjustment(id: number) {
      this.adjustments[id].checked = false
    },
  },
  getters: {
    getAdjustmentIdsByFilter(): {
      categoryIds: Record<string, Array<number>>
      copyTypes: Record<string, Array<number>>
      tagIds: Record<string, Array<number>>
    } {
      return this.getUnsavedAdjustments.reduce<{
        categoryIds: Record<string, Array<number>>
        copyTypes: Record<string, Array<number>>
        tagIds: Record<string, Array<number>>
      }>(
        (acc, adjustment) => {
          ;['categoryIds', 'copyTypes', 'tagIds'].forEach((k) => {
            const adjustmentFilterKey = k as AdjustmentFilterKeys
            const adjustmentFilter = adjustment[adjustmentFilterKey]

            adjustmentFilter.forEach((filterId) => {
              if (filterId === null) {
                return acc
              }

              if (!acc[adjustmentFilterKey][filterId]) {
                acc[adjustmentFilterKey][filterId] = []
              }

              acc[adjustmentFilterKey][filterId].push(adjustment.id)
            })
          })

          return acc
        },
        {
          categoryIds: {},
          copyTypes: {},
          tagIds: {},
        }
      )
    },
    getAdjustmentIdsByFilterLength(): {
      categoryIds: Record<string, number>
      copyTypes: Record<string, number>
      tagIds: Record<string, number>
    } {
      return Object.keys(this.getAdjustmentIdsByFilter).reduce<{
        categoryIds: Record<string, number>
        copyTypes: Record<string, number>
        tagIds: Record<string, number>
      }>(
        (acc, key) => {
          if (isAdjustmentFilterKey(key)) {
            const filterData = this.getAdjustmentIdsByFilter[key]

            Object.keys(filterData).forEach((subkey) => {
              acc[key][subkey] = filterData[subkey].length
            })
          }

          return acc
        },
        {
          categoryIds: {},
          copyTypes: {},
          tagIds: {},
        }
      )
    },
    getAdjustmentsLength(state): number {
      return state.adjustmentOrder.length
    },
    getCheckedAdjustments(state) {
      return Object.values(state.adjustments).filter(
        (adjustment) => adjustment.checked
      )
    },
    getCheckedAdjustmentsLength(): number {
      return this.getCheckedAdjustments.length
    },
    getCheckedAdjustmentIdsByFilterLength(state): {
      categoryIds: Record<string, number>
      copyTypes: Record<string, number>
      tagIds: Record<string, number>
    } {
      return Object.keys(this.getAdjustmentIdsByFilter).reduce<{
        categoryIds: Record<string, number>
        copyTypes: Record<string, number>
        tagIds: Record<string, number>
      }>(
        (acc, key) => {
          if (isAdjustmentFilterKey(key)) {
            const filterData = this.getAdjustmentIdsByFilter[key]

            Object.keys(filterData).forEach((subkey) => {
              acc[key][subkey] = filterData[subkey].filter(
                (adjustmentId) => state.adjustments[adjustmentId].checked
              ).length
            })
          }

          return acc
        },
        {
          categoryIds: {},
          copyTypes: {},
          tagIds: {},
        }
      )
    },
    getExternalAdjustments(): Adjustment[] {
      return this.getUnsavedAdjustments.filter(
        (adjustment) => adjustment.category === 'external'
      )
    },
    getExternalPostedAdjustments(): Adjustment[] {
      return this.getPostedAdjustments.filter(
        (adjustment) => adjustment.category === 'external'
      )
    },
    getInternalAdjustments(): Adjustment[] {
      return this.getUnsavedAdjustments.filter(
        (adjustment) => adjustment.category === 'internal'
      )
    },
    getInternalPostedAdjustments(): Adjustment[] {
      return this.getPostedAdjustments.filter(
        (adjustment) => adjustment.category === 'internal'
      )
    },
    getPostedAdjustments(state) {
      return Object.values(state.adjustments).filter(
        (adjustment) => adjustment.postedAt
      )
    },
    getUnsavedAdjustments(state) {
      return Object.values(state.adjustments).filter(
        (adjustment) => !adjustment.postedAt
      )
    },
    hasAccountSelected(state): boolean {
      return this.isAccountNeeded && state.selectedAccountId !== 0
    },
    hasForwardedOrReversedAdjustments(state): boolean {
      const differentCopyTypes = state.copyTypes.map(function (copyType) {
        return copyType.id
      })

      const validDifferentTypes = Object.values(state.adjustments).filter(
        (adjustment) => {
          return (
            adjustment.originalAdjustmentType &&
            differentCopyTypes.indexOf(adjustment.originalAdjustmentType) >= 0
          )
        }
      )

      return validDifferentTypes.length > 0
    },
    hasLedgerSelected(state): boolean {
      return state.selectedLedgerId !== 'placeholder'
    },
    isAccountNeeded(state) {
      return state.copyType === 'forward'
    },
    isLedgerDisabled(): boolean {
      return (
        (this.isAccountNeeded && !this.hasAccountSelected) ||
        this.isLoadingLedger
      )
    },
    isLoadingLedger(state) {
      return state.loading.ledger
    },
    isSubmitDisabled(state): boolean {
      return (
        !this.hasLedgerSelected ||
        this.getCheckedAdjustmentsLength === 0 ||
        state.loading.submit
      )
    },
    shouldShowCopyTypes(): boolean {
      return this.hasLedgerSelected && this.hasForwardedOrReversedAdjustments
    },
    shouldShowTags(state): boolean {
      return this.hasLedgerSelected && state.tags.length > 0
    },
  },
})
